import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const themes = {
  light: {
    default: {
      name: 'default',
      background: '#FFFFFF',
      text: '#1F2937',
      primary: '#F0EDE7',
      primaryHover: '#E5E2DC',
      secondary: '#F9F9F9',
      border: '#E5E7EB',
      accent: '#4B5563',
      error: '#EF4444',
      success: '#10B981',
      hover: '#E5E7EB'
    },
    creamPaper: {
      name: 'creamPaper',
      background: '#FDFAF6',
      text: '#1A1A1A',
      primary: '#F5EFE6',
      primaryHover: '#EBE5DC',
      secondary: '#FAF7F2',
      border: '#E6DFD5',
      accent: '#8C8C8C',
      error: '#DC2626',
      success: '#059669',
      hover: '#E2DCD3'
    },
    crystalClear: {
      name: 'crystalClear',
      background: '#F8FAFC',
      text: '#0F172A',
      primary: '#F1F5F9',
      primaryHover: '#E2E8F0',
      secondary: '#F4F7FB',
      border: '#CBD5E1',
      accent: '#64748B',
      error: '#EF4444',
      success: '#10B981',
      hover: '#E2E8F0'
    },
    lavenderMist: {
      name: 'lavenderMist',
      background: '#F8F7FF',
      text: '#1E1B2C',
      primary: '#F0EEFF',
      primaryHover: '#E5E3F5',
      secondary: '#F5F4FC',
      border: '#DCD9F0',
      accent: '#9995AD',
      error: '#E11D48',
      success: '#059669',
      hover: '#E8E6F7'
    },
    sageLight: {
      name: 'sageLight',
      background: '#F7F9F8',
      text: '#1C2420',
      primary: '#EFF3F0',
      primaryHover: '#E4E9E6',
      secondary: '#F3F6F4',
      border: '#D8E0DC',
      accent: '#8A958E',
      error: '#DC2626',
      success: '#059669',
      hover: '#DFE5E1'
    },
    morningFrost: {
      name: 'morningFrost',
      background: '#FFFFFF',
      text: '#111827',
      primary: '#F9FAFB',
      primaryHover: '#F3F4F6',
      secondary: '#FCFCFD',
      border: '#E5E7EB',
      accent: '#6B7280',
      error: '#DC2626',
      success: '#059669',
      hover: '#ECEDEF'
    }
  },
  dark: {
    deepOcean: {
      name: 'deepOcean',
      background: '#0F172A',
      text: '#E2E8F0',
      primary: '#1E293B',
      primaryHover: '#334155',
      secondary: '#1A2234',
      border: '#2A3B55',
      accent: '#64748B',
      error: '#DC2626',
      success: '#059669',
      hover: '#2C4366'
    },
    volcanicNight: {
      name: 'volcanicNight',
      background: '#18181B',
      text: '#FAFAFA',
      primary: '#27272A',
      primaryHover: '#3F3F46',
      secondary: '#202023',
      border: '#393942',
      accent: '#71717A',
      error: '#EF4444',
      success: '#10B981',
      hover: '#52525B'
    },
    forestDark: {
      name: 'forestDark',
      background: '#1B2428',
      text: '#ECF2F5',
      primary: '#2A363C',
      primaryHover: '#3A474E',
      secondary: '#243035',
      border: '#324147',
      accent: '#8B9CA4',
      error: '#F87171',
      success: '#34D399',
      hover: '#405158'
    },
    cosmicPurple: {
      name: 'cosmicPurple',
      background: '#1A1625',
      text: '#F8F7FC',
      primary: '#2A2438',
      primaryHover: '#3B334D',
      secondary: '#221D2E',
      border: '#362C47',
      accent: '#9D94B3',
      error: '#FB7185',
      success: '#4ADE80',
      hover: '#443A5C'
    },
    carbon: {
      name: 'carbon',
      background: '#151515',
      text: '#FAFAFA',
      primary: '#222222',
      primaryHover: '#2E2E2E',
      secondary: '#1A1A1A',
      border: '#333333',
      accent: '#808080',
      error: '#FF4D4D',
      success: '#00CC66',
      hover: '#404040'
    }
  }
};

const getSystemTheme = () => {
  const systemCategory = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  // Ensure the system category is valid
  return themes[systemCategory] ? systemCategory : 'light';
};

// Add a function to get custom themes from localStorage
const getCustomThemes = () => {
  try {
    return JSON.parse(localStorage.getItem('customThemes')) || { light: {}, dark: {} };
  } catch (error) {
    console.error('Error parsing custom themes:', error);
    return { light: {}, dark: {} };
  }
};

// Add a function to save custom themes to localStorage
const saveCustomThemes = (customThemes) => {
  localStorage.setItem('customThemes', JSON.stringify(customThemes));
};

// Keep original themes separate and immutable
const originalThemes = {
  light: { ...themes.light },
  dark: { ...themes.dark }
};

export const ThemeProvider = ({ children }) => {
  const [themeState, setThemeState] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const savedDefaults = JSON.parse(localStorage.getItem('themeDefaults') || '{}');
    const customThemes = getCustomThemes();
    const isSystem = savedTheme === 'system';
    
    // Create merged themes object
    const mergedThemes = {
      light: { ...originalThemes.light, ...customThemes.light },
      dark: { ...originalThemes.dark, ...customThemes.dark }
    };
    
    let activeTheme;
    if (isSystem) {
      const systemCategory = getSystemTheme();
      activeTheme = systemCategory === 'dark' 
        ? (savedDefaults.dark || mergedThemes.dark.deepOcean)
        : (savedDefaults.light || mergedThemes.light.default);
    } else if (savedTheme) {
      const [category, name] = savedTheme.split('.');
      activeTheme = mergedThemes[category]?.[name] || mergedThemes.light.default;
    } else {
      activeTheme = mergedThemes.light.default;
    }

    return {
      theme: activeTheme,
      isSystemTheme: isSystem,
      defaultLight: savedDefaults.light || mergedThemes.light.default,
      defaultDark: savedDefaults.dark || mergedThemes.dark.deepOcean,
      customThemes,
      mergedThemes
    };
  });

  // Debug log for themeState changes
  useEffect(() => {
    console.log('themeState updated:', themeState);
  }, [themeState]);

  // Effect for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      if (themeState.isSystemTheme) {
        const systemCategory = getSystemTheme();
        const newTheme = systemCategory === 'dark' ? themeState.defaultDark : themeState.defaultLight;
        console.log('System theme change:', { systemCategory, newTheme });
        setThemeState(prev => ({
          ...prev,
          theme: newTheme
        }));
      }
    };

    // Initial system theme check
    if (themeState.isSystemTheme) {
      const systemCategory = getSystemTheme();
      const newTheme = systemCategory === 'dark' ? themeState.defaultDark : themeState.defaultLight;
      console.log('Initial system theme check:', { systemCategory, newTheme });
      setThemeState(prev => ({
        ...prev,
        theme: newTheme
      }));
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [themeState.isSystemTheme, themeState.defaultLight, themeState.defaultDark]);

  const deleteTheme = (themeName, category) => {
    console.log('Deleting theme:', { themeName, category });
    
    // Don't allow deletion of built-in themes
    const isBuiltIn = originalThemes[category][themeName] !== undefined;
    
    if (isBuiltIn) {
      console.warn('Cannot delete built-in theme');
      return false;
    }

    // Create new custom themes object without the deleted theme
    const newCustomThemes = {
      ...themeState.customThemes,
      [category]: {
        ...themeState.customThemes[category]
      }
    };
    delete newCustomThemes[category][themeName];

    // Create new merged themes
    const newMergedThemes = {
      light: { ...originalThemes.light, ...newCustomThemes.light },
      dark: { ...originalThemes.dark, ...newCustomThemes.dark }
    };

    // Update state and localStorage
    setThemeState(prev => ({
      ...prev,
      customThemes: newCustomThemes,
      mergedThemes: newMergedThemes
    }));
    saveCustomThemes(newCustomThemes);

    // If the deleted theme was active, switch to default
    if (themeState.theme.name === themeName) {
      setThemeWithSystem(category === 'dark' ? newMergedThemes.dark.deepOcean : newMergedThemes.light.default);
    }

    return true;
  };

  const setThemeWithSystem = (newTheme) => {
    console.log('setThemeWithSystem called with:', newTheme);
    
    if (newTheme === 'system') {
      const systemCategory = getSystemTheme();
      const systemTheme = systemCategory === 'dark' ? themeState.defaultDark : themeState.defaultLight;
      console.log('Setting system theme:', { systemCategory, systemTheme });
      setThemeState(prev => ({
        ...prev,
        theme: systemTheme,
        isSystemTheme: true
      }));
      localStorage.setItem('theme', 'system');
    } else {
      const category = newTheme.category || Object.entries(themeState.mergedThemes).find(([_, categoryThemes]) =>
        Object.values(categoryThemes).some(t => t.name === newTheme.name)
      )?.[0];

      if (category) {
        console.log('Setting specific theme:', { category, newTheme });
        
        // Add the theme to custom themes if it's a new theme
        if (newTheme.category) {
          const newCustomThemes = {
            ...themeState.customThemes,
            [category]: {
              ...themeState.customThemes[category],
              [newTheme.name]: newTheme
            }
          };
          
          // Create new merged themes
          const newMergedThemes = {
            light: { ...originalThemes.light, ...newCustomThemes.light },
            dark: { ...originalThemes.dark, ...newCustomThemes.dark }
          };

          setThemeState(prev => ({
            ...prev,
            theme: newTheme,
            isSystemTheme: false,
            customThemes: newCustomThemes,
            mergedThemes: newMergedThemes
          }));
          saveCustomThemes(newCustomThemes);
        } else {
          setThemeState(prev => ({
            ...prev,
            theme: newTheme,
            isSystemTheme: false
          }));
        }
        
        localStorage.setItem('theme', `${category}.${newTheme.name}`);
      }
    }
  };

  const setDefaultTheme = (category, theme) => {
    console.log(`Setting default ${category} theme:`, theme);
    setThemeState(prev => ({
      ...prev,
      [category === 'dark' ? 'defaultDark' : 'defaultLight']: theme,
      // If system theme is active, update the current theme if needed
      ...(prev.isSystemTheme && getSystemTheme() === category ? { theme } : {})
    }));
    
    // Save defaults to localStorage
    const currentDefaults = JSON.parse(localStorage.getItem('themeDefaults') || '{}');
    localStorage.setItem('themeDefaults', JSON.stringify({
      ...currentDefaults,
      [category]: theme
    }));
  };

  // Effect for applying theme
  useEffect(() => {
    if (themeState.theme) {
      console.log('Applying theme to document:', themeState.theme);
      Object.entries(themeState.theme).forEach(([key, value]) => {
        if (key !== 'name') {
          document.documentElement.style.setProperty(`--${key}`, value);
        }
      });
    }
  }, [themeState.theme]);

  // Create context value with merged themes
  const contextValue = {
    theme: themeState.theme,
    setTheme: setThemeWithSystem,
    setDefaultTheme,
    deleteTheme,
    themes: themeState.mergedThemes, // Use merged themes here instead of the original themes
    isSystemTheme: themeState.isSystemTheme,
    defaultLight: themeState.defaultLight,
    defaultDark: themeState.defaultDark
  };

  console.log('Providing context value:', contextValue);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 